// v4 (spec-v4-structure-and-stage.md 3.3, 5.3 "Upload sheet", 7.1-7.4; ticket V4-A2): one upload sheet for every kind
// of file at any level: pictures (products, places, props, drawings, a person for a character), logos, songs and theme
// music, sound effects, clips, intros and outros.
//
//   Add files to Milo & Friends
//   [ Choose files ]   or drop here           (drag-and-drop on desktop; several files at once)
//   Pictures 20 MB · Songs 100 MB · Sound effects 20 MB · Clips 500 MB
//   1.2 of 5 GB used ███░░░
//   ☐ I own these files or have permission…   (once per account: UPLOAD_TERMS_CONSENT, and again on a 428)
//   ☐ This is me, or I have this person's…    (a photo of a person: PERSON_PHOTO_CONSENT)
//   kitchen.jpg   [Place ▾]   ████████░ 80%   ✕ (cancel)
//   theme.mp3     done · tagged: warm, acoustic · 2:41   Edit
//
// It reuses api.uploadFile (start -> PUT with progress -> complete). Files are checked before a byte moves (type,
// size, room left) and the api's answers (too big, too long, quota, the 428) come back as one plain line on the file's
// row. They go one at a time; ✕ cancels the one sending (or drops one waiting). Auto tags arrive a moment after each
// upload (an "asset" event), and the row picks them up.
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { api, ApiError, type StorageInfo } from "../api";
import { director, pauseCall } from "../director";
import { fmtBytes } from "../download";
import { PERSON_PHOTO_CONSENT, UPLOAD_TERMS_CONSENT, type ConsentCopy } from "../legal/content";
import {
  checkFile,
  DEFAULT_LIMITS,
  doneLine,
  guessWhat,
  limitsLine,
  mediaOf,
  quotaCheck,
  scopeKey,
  storageMeter,
  uploadErrorText,
  uploadScope,
  WHATS,
  whatsFor,
  type LibScope,
  type UploadKind,
  type UploadLimit,
  type UploadWhat,
} from "../library";
import { bump, toast, useRefresh, useStore } from "../store";
import type { Asset } from "../types";
import { Icon, Sheet } from "./bits";
import { openPicker } from "../picker";
import { DOC_ACCEPT, docDoneLine, docProblem, isDocument, pastedFiles } from "../documents";
import type { DocumentAdded } from "../types";

type RowState = "ready" | "sending" | "done" | "failed";
/** doc: a PDF / text / Markdown file whose words join the video's sources (2026-11-03); docDone: what came of it */
type Row = { key: number; file: File; what: UploadWhat | null; state: RowState; sent: number; error: string | null; asset: Asset | null; doc?: boolean; docDone?: DocumentAdded };

export type UploadSheetProps = {
  /** where the files go (the library's scope, a collection, a video...) */
  scope: LibScope;
  /** "Milo & Friends", "your library" */
  scopeName: string;
  /** what the files are when the caller knows ("Add an intro"); a file of another sort is still guessed */
  preset?: UploadWhat | null;
  /** files dropped on the page before the sheet opened */
  files?: File[];
  /** one file only (a logo, an intro) */
  single?: boolean;
  onClose: () => void;
  /** after a batch: the items made. `personConsent`: the person-photo box was ticked for them. */
  onDone?: (items: Asset[], ctx: { personConsent: boolean }) => void;
  /** "Edit" on a finished row */
  onEdit?: (a: Asset) => void;
};

let seq = 0;
const NOT_A_FILE_WE_TAKE = "That kind of file can't go in the library. Add pictures (PNG, JPEG, WebP), songs and sound effects (MP3, WAV, M4A), clips (MP4, MOV), or a PDF for a video's sources.";
const ACCEPT: Record<"image" | "audio" | "video" | "any", string> = {
  image: "image/png,image/jpeg,image/webp",
  audio: "audio/*,.mp3,.wav,.m4a",
  video: "video/mp4,video/quicktime,.mp4,.mov",
  any: "image/png,image/jpeg,image/webp,audio/*,.mp3,.wav,.m4a,video/mp4,video/quicktime,.mov",
};
/** 2026-11-03: on a video, the picker takes documents too (their words join the sources) */
const acceptFor = (media: keyof typeof ACCEPT, onVideo: boolean) => (onVideo && media === "any" ? `${ACCEPT.any},${DOC_ACCEPT}` : ACCEPT[media]);

/** The per-kind limits the api sent (GET /api/me/storage), over the defaults. */
function limitsOf(info: StorageInfo | null): Record<UploadKind, UploadLimit> {
  const out = { ...DEFAULT_LIMITS };
  for (const k of Object.keys(out) as UploadKind[]) {
    const l = info?.limits?.[k];
    if (l && Array.isArray(l.mimes) && typeof l.maxBytes === "number") out[k] = { mimes: l.mimes, maxBytes: l.maxBytes, maxSeconds: l.maxSeconds };
  }
  return out;
}

export function UploadSheet({ scope, scopeName, preset = null, files, single = false, onClose, onDone, onEdit }: UploadSheetProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [info, setInfo] = useState<StorageInfo | null>(null);
  const [terms, setTerms] = useState<boolean | null>(null);
  const [ticked, setTicked] = useState(false);
  const [personTicked, setPersonTicked] = useState(false);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const ctrl = useRef<AbortController | null>(null);
  const stop = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const limits = useMemo(() => limitsOf(info), [info]);
  // 2026-11-03 (James's report): a voice call bills while they hunt for files; offer to pause it
  const onCall = useStore((s) => s.director !== "off" && s.director !== "error" && !s.directorTyped);

  // the terms checkbox up front (GET /api/me/storage), the limits and the meter
  useEffect(() => {
    let live = true;
    api
      .myStorage()
      .then((x) => {
        if (!live) return;
        setInfo(x);
        setTerms(x.termsAccepted);
      })
      .catch(() => live && setTerms(false));
    return () => {
      live = false;
    };
  }, []);

  // 2026-11-03: a document goes on a video (its words join the sources), never in the library
  const projectId = scope.level === "project" ? scope.id : null;
  const make = (file: File): Row => {
    if (isDocument(file)) {
      const err = docProblem(file, projectId);
      return { key: ++seq, file, what: null, state: err ? "failed" : "ready", sent: 0, error: err, asset: null, doc: true };
    }
    const what = guessWhat(file, preset);
    return { key: ++seq, file, what, state: what ? "ready" : "failed", sent: 0, error: what ? null : NOT_A_FILE_WE_TAKE, asset: null };
  };
  const add = (list: FileList | File[] | null | undefined) => {
    const picked = Array.from(list ?? []);
    if (!picked.length) return;
    setRows((rs) => {
      const next = [...rs.filter((r) => !single || r.state === "done"), ...(single ? picked.slice(0, 1) : picked).map(make)];
      return next;
    });
  };
  useEffect(() => add(files), []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2026-11-03 (James's report): paste a screenshot (or copied files) straight into the open sheet. Typing in a box
  // on the sheet keeps its own paste.
  const addRef = useRef(add);
  addRef.current = add;
  const runningRef = useRef(false);
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const picked = pastedFiles(e.clipboardData);
      if (!picked.length || runningRef.current) return;
      e.preventDefault();
      addRef.current(picked);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  // the checks before a byte moves, per row (a row's "what" can change, so these are derived, not stored)
  const problem = (r: Row) => (r.doc ? (r.state === "failed" || r.state === "ready" ? docProblem(r.file, projectId) : null) : r.state === "failed" || r.state === "ready" ? (r.what ? checkFile(r.file, r.what, limits) : NOT_A_FILE_WE_TAKE) : null);
  const waiting = rows.filter((r) => r.state === "ready" && !problem(r));
  const files_ = waiting.filter((r) => !r.doc); // documents aren't stored: no terms box, no room needed
  const needPerson = rows.some((r) => r.what === "person" && r.state !== "done");
  const termsNeeded = terms === false && (files_.length > 0 || !waiting.length);
  const quota = info ? quotaCheck(files_.reduce((n, r) => n + r.file.size, 0), info.usedBytes, info.quotaBytes) : null;
  const blocked = (termsNeeded && !ticked) || (needPerson && !personTicked) || !!quota;

  const patch = (key: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const run = async () => {
    if (running || !waiting.length || blocked) return;
    setRunning(true);
    runningRef.current = true;
    stop.current = false;
    const made: Asset[] = [];
    const docs: { name: string; r: DocumentAdded }[] = [];
    for (const r of waiting) {
      if (stop.current) break;
      if (r.doc) {
        const c = new AbortController();
        ctrl.current = c;
        patch(r.key, { state: "sending", sent: 0.5, error: null });
        try {
          const res = await api.addDocument(projectId!, r.file, c.signal);
          docs.push({ name: r.file.name, r: res });
          patch(r.key, { state: "done", sent: 1, docDone: res });
        } catch (e) {
          const err = e instanceof ApiError ? e : new ApiError((e as Error)?.message ?? "", 0);
          if (err.code === "CANCELLED") {
            setRows((rs) => rs.filter((x) => x.key !== r.key));
            continue;
          }
          patch(r.key, { state: "failed", sent: 0, error: err.message || "That document couldn't be added." });
        }
        continue;
      }
      const w = WHATS[r.what!];
      const c = new AbortController();
      ctrl.current = c;
      patch(r.key, { state: "sending", sent: 0, error: null });
      try {
        const a = await api.uploadFile(r.file, { kind: w.kind, ...(w.role ? { role: w.role } : {}), scope: uploadScope(scope), ...(ticked ? { termsAccepted: true } : {}) }, (sent) => patch(r.key, { sent }), c.signal);
        made.push(a);
        setTerms(true);
        setInfo((x) => (x ? { ...x, usedBytes: x.usedBytes + r.file.size, termsAccepted: true } : x));
        patch(r.key, { state: "done", sent: 1, asset: a });
      } catch (e) {
        const err = e instanceof ApiError ? e : new ApiError((e as Error)?.message ?? "", 0);
        if (err.code === "CANCELLED") {
          setRows((rs) => rs.filter((x) => x.key !== r.key));
          continue;
        }
        if (err.code === "TERMS_NEEDED") {
          // the 428: ask for the box, keep the file waiting
          setTerms(false);
          setTicked(false);
          patch(r.key, { state: "ready", sent: 0, error: uploadErrorText(err, r.what!, limits) });
          break;
        }
        patch(r.key, { state: "failed", sent: 0, error: uploadErrorText({ status: err.status, code: err.code, message: err.message, details: err.details }, r.what!, limits) });
        if (err.code === "QUOTA_EXCEEDED" || err.code === "RATE_LIMITED") break;
      }
    }
    ctrl.current = null;
    setRunning(false);
    runningRef.current = false;
    if (docs.length) {
      bump("project");
      const said = docs.filter((x) => !x.r.full);
      director.context(
        said.length
          ? `The user added ${said.map((x) => `"${x.name}"`).join(", ")} to this video's sources (the brief's facts and notes; ${said.reduce((n, x) => n + x.r.added, 0)} characters${said.some((x) => x.r.cut) ? ", some cut to fit" : ""}). It starts: "${said[0]!.r.preview.slice(0, 300).replace(/\s+/g, " ")}". Use these facts; read_script / the brief has them all.`
          : "The user tried to add a document, but the video's sources are full.",
      );
    }
    if (made.length) {
      bump("library");
      director.context(`The user added ${made.length === 1 ? `"${made[0]!.name}"` : `${made.length} files`} to ${scope.level === "account" ? "their library" : `the library of ${scopeName}`} (${scopeKey(scope)}).`);
      onDone?.(made, { personConsent: personTicked });
    }
  };

  const cancel = (r: Row) => {
    if (r.state === "sending") ctrl.current?.abort();
    else setRows((rs) => rs.filter((x) => x.key !== r.key));
  };
  const close = () => {
    stop.current = true;
    ctrl.current?.abort();
    onClose();
  };

  // auto tags land a moment after each upload ("asset" events bump the library): pick them up for finished rows
  const tick = useRefresh("library");
  const doneIds = rows.filter((r) => r.asset && !r.asset.autoTags?.length).map((r) => r.asset!.id);
  useEffect(() => {
    if (!doneIds.length) return;
    let live = true;
    api
      .library({ scope: scopeKey(scope), here: true })
      .then((items) => {
        if (!live) return;
        const by = new Map(items.map((a) => [a.id, a]));
        setRows((rs) => rs.map((r) => (r.asset && by.has(r.asset.id) ? { ...r, asset: by.get(r.asset.id)! } : r)));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!running) add(e.dataTransfer?.files);
  };
  const meter = info ? storageMeter(info.usedBytes, info.quotaBytes) : null;
  const presetMedia = preset ? WHATS[preset].media : null;
  const one = preset ? WHATS[preset].label.toLowerCase() : "";
  const title = preset && single ? `Add ${/^[aeiou]/.test(one) ? "an" : "a"} ${one} to ${scopeName}` : `Add files to ${scopeName}`;

  return (
    <Sheet title={title} onClose={close} className="upsheet">
      <div className="sheet-body up-body">
        <div
          className={`up-drop${over ? " is-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={drop}
        >
          <button type="button" className="btn btn-primary" onClick={() => openPicker(input.current)} disabled={running} data-autofocus>
            <Icon name="plus" size={18} /> {single ? "Choose a file" : "Choose files"}
          </button>
          <span className="up-drop-hint">or drop {single ? "it" : "them"} here</span>
          <input ref={input} type="file" hidden multiple={!single} accept={acceptFor(presetMedia ?? "any", !!projectId)} onChange={(e) => (add(e.target.files), (e.target.value = ""))} />
        </div>
        <p className="up-paste muted">{single ? "Or paste it here." : `Or paste screenshots here${projectId ? ". A PDF's words join the video's sources." : "."}`}</p>
        {projectId && !single && !preset && <WebsiteRow projectId={projectId} />}
        {onCall && (
          <p className="up-pause muted">
            <button type="button" className="btn btn-quiet btn-small" data-action="call.pause" onClick={() => pauseCall()}>
              <Icon name="pause" size={14} /> Pause the call while you pick
            </button>{" "}
            Your Director picks up where you left off.
          </p>
        )}
        <p className="up-limits muted">{limitsLine(limits)}</p>
        {meter && <StorageMeter info={info!} />}

        {termsNeeded && <Consent copy={UPLOAD_TERMS_CONSENT} checked={ticked} onChange={setTicked} className="up-terms" />}
        {needPerson && <Consent copy={PERSON_PHOTO_CONSENT} checked={personTicked} onChange={setPersonTicked} className="up-person" />}

        {!!rows.length && (
          <ul className="up-rows" aria-label="Files">
            {rows.map((r) => (
              <UploadRow key={r.key} r={r} problem={problem(r)} running={running} onWhat={(what) => patch(r.key, { what, error: null, state: "ready" })} onCancel={() => cancel(r)} onEdit={onEdit} />
            ))}
          </ul>
        )}

        {quota && (
          <p className="up-quota" role="alert">
            {quota}
          </p>
        )}

        <div className="up-foot">
          {rows.some((r) => r.state === "done") && !waiting.length && !running ? (
            <button type="button" className="btn btn-primary btn-block" onClick={close}>
              Done
            </button>
          ) : (
            <button data-action="upload.go" type="button" className="btn btn-primary btn-block" onClick={run} disabled={running || !waiting.length || blocked}>
              {running ? "Uploading…" : waiting.length ? `Upload ${waiting.length === 1 ? "1 file" : `${waiting.length} files`}` : "Upload"}
            </button>
          )}
          {blocked && !running && !!waiting.length && <p className="up-why muted">{quota ? "Not enough room." : termsNeeded && !ticked ? "Tick the box above first." : "Tick the box about the person first."}</p>}
        </div>
      </div>
    </Sheet>
  );
}

/** 2026-11-03 (James's reports): read a public web page for this video (words to its sources, pictures to its library). */
function WebsiteRow({ projectId }: { projectId: string }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);
  const go = async () => {
    const u = url.trim();
    if (!u || busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const r = await api.readWebsite(projectId, u);
      const pics = r.images.length ? ` · ${r.images.length} product picture${r.images.length === 1 ? "" : "s"} in the library` : "";
      setSaid({ ok: true, text: r.full ? "The sources are full, so its words weren't added. Trim them in the brief." : `Read ${r.page.title || r.page.siteName}: ${r.added.toLocaleString()} characters to the sources${r.cut ? " (the rest didn't fit)" : ""}${pics}.` });
      setUrl("");
      bump("project", "library");
      director.context(
        `The user had the studio read ${r.page.url} for this video: "${r.page.title}"${r.page.description ? ` ("${r.page.description.slice(0, 160)}")` : ""}. ${r.full ? "The sources were full." : `Its words joined the sources (${r.added} characters).`}${r.images.length ? ` Product pictures in the video's library: ${r.images.map((a) => `"${a.name}" (item id ${a.id})`).join("; ")}.` : ""}${r.page.themeColor ? ` Theme colour ${r.page.themeColor}.` : ""} Offer to fill the brief and brand from it.`,
      );
    } catch (e) {
      setSaid({ ok: false, text: e instanceof ApiError ? e.message : "That website couldn't be read." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="up-web"
      onSubmit={(e) => {
        e.preventDefault();
        void go();
      }}
    >
      <label className="field up-web-field">
        <span className="field-label">Or read a website</span>
        <span className="up-web-row">
          <input type="text" inputMode="url" autoComplete="url" placeholder="yourshop.com" value={url} onChange={(e) => setUrl(e.target.value)} disabled={busy} aria-label="Website address" />
          <button type="submit" className="btn btn-quiet btn-small" data-action="website.read" disabled={busy || !url.trim()}>
            {busy ? "Reading…" : "Read it"}
          </button>
        </span>
        <span className="field-hint">Public pages only. Its words join the sources and its product pictures go in the library.</span>
      </label>
      {said && <p className={said.ok ? "up-done" : "up-err"}>{said.text}</p>}
    </form>
  );
}

function UploadRow({ r, problem, running, onWhat, onCancel, onEdit }: { r: Row; problem: string | null; running: boolean; onWhat: (w: UploadWhat) => void; onCancel: () => void; onEdit?: (a: Asset) => void }) {
  const media = r.doc ? null : mediaOf(r.file);
  const options = media ? whatsFor(media) : [];
  const error = r.error ?? problem;
  const icon = r.doc ? "script" : media === "audio" ? "music" : media === "video" ? "film" : "image";
  const canPick = (r.state === "ready" || r.state === "failed") && !!options.length && !running;
  return (
    <li className={`up-row is-${r.state}${error ? " has-error" : ""}`}>
      <span className="up-ic" aria-hidden>
        {r.state === "done" ? <Icon name="check" size={18} /> : <Icon name={icon} size={18} />}
      </span>
      <div className="up-main">
        <span className="up-name">{r.file.name}</span>
        {r.state !== "done" && (
          <div className="up-sub">
            {canPick ? (
              <label className="up-what">
                <span className="sr-only">What {r.file.name} is</span>
                <select value={r.what ?? ""} onChange={(e) => onWhat(e.target.value as UploadWhat)}>
                  {options.map((w) => (
                    <option key={w} value={w}>
                      {WHATS[w].label}
                    </option>
                  ))}
                </select>
              </label>
            ) : r.doc ? (
              <span className="up-what-text muted">Sources for this video</span>
            ) : (
              r.what && <span className="up-what-text muted">{WHATS[r.what].label}</span>
            )}
            {r.state === "sending" ? (
              <div className="up-prog" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(r.sent * 100)} aria-label={`Uploading ${r.file.name}`}>
                <span className="up-bar">
                  <span style={{ width: `${Math.round(r.sent * 100)}%` }} />
                </span>
                <span className="up-pct">{Math.round(r.sent * 100)}%</span>
              </div>
            ) : (
              <span className="up-size muted">{fmtBytes(r.file.size)}</span>
            )}
          </div>
        )}
        {r.state === "done" && r.docDone ? (
          <p className="up-done">
            <span>{r.docDone.full ? docDoneLine(r.docDone) : `done · ${docDoneLine(r.docDone)}`}</span>
          </p>
        ) : r.state === "done" && r.asset ? (
          <p className="up-done">
            <span>done · {doneLine(r.asset)}</span>
            {onEdit && (
              <button type="button" className="linkish" onClick={() => onEdit(r.asset!)}>
                Edit
              </button>
            )}
          </p>
        ) : (
          error && r.state !== "sending" && <p className="up-err">{error}</p>
        )}
      </div>
      {r.state !== "done" && (
        <button type="button" className="icon-btn up-x" onClick={onCancel} aria-label={r.state === "sending" ? `Cancel ${r.file.name}` : `Remove ${r.file.name}`} title={r.state === "sending" ? "Cancel" : "Remove"} disabled={running && r.state !== "sending"}>
          <Icon name="close" size={16} />
        </button>
      )}
    </li>
  );
}

function Consent({ copy, checked, onChange, className }: { copy: ConsentCopy; checked: boolean; onChange: (v: boolean) => void; className: string }) {
  return (
    <label className={`up-check ${className}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {copy.label}{" "}
        <a href={copy.link.href} target="_blank" rel="noopener noreferrer">
          {copy.link.text}
        </a>
      </span>
    </label>
  );
}

/** "1.2 of 5 GB used" with a bar: uploads only (what the Director makes doesn't count). */
export function StorageMeter({ info, className = "" }: { info: StorageInfo; className?: string }) {
  const m = storageMeter(info.usedBytes, info.quotaBytes);
  return (
    <div className={`up-meter is-${m.tone} ${className}`}>
      <span className="up-meter-text">
        <span>Your uploads</span> <span className="muted">{m.text}</span>
      </span>
      {info.quotaBytes !== null && (
        <span className="up-meter-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(m.share * 100)} aria-label="Upload storage used">
          <span style={{ width: `${Math.max(1, Math.round(m.share * 100))}%` }} />
        </span>
      )}
    </div>
  );
}

/** Whether a drag carries files (not text or a link dragged across the page). */
export const dragHasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** For callers that toast instead of showing a row (one file, no sheet). */
export function toastUploadError(e: unknown, what: UploadWhat) {
  const err = e instanceof ApiError ? e : new ApiError((e as Error)?.message ?? "", 0);
  toast(uploadErrorText({ status: err.status, code: err.code, message: err.message, details: err.details }, what), "bad");
}
